import { useCallback, useMemo, useState } from 'react';
import { ButtonVariants, Table } from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { buildTableLabels } from '@/config/tableLabels';
import { useTableState, useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useGetBackToBackAccountsQuery } from '../../api/backToBackAccountsApi';
import { buildBackToBackAccountsColumns } from '../../constants/backToBackAccountsColumns';
import { BackToBackAccountModal } from './BackToBackAccountModal';

const getAccountRowId = (row) => row.id;

export const BackToBackAccountsTable = () => {
  const t = useTranslations();
  const table = useTableState();
  const [editTarget, setEditTarget] = useState(null);

  const { data, isFetching } = useGetBackToBackAccountsQuery(table.queryParams);
  const rows = data?.rows ?? EMPTY_LIST;

  const handleAdd = useCallback(() => setEditTarget({}), []);
  const handleEdit = useCallback((row) => setEditTarget({ account: row }), []);
  const handleCloseModal = useCallback(() => setEditTarget(null), []);

  const handleSortChange = useCallback(
    (next) => {
      table.setSort(next ? { field: next.id, direction: next.dir } : null);
      table.setPage(1);
    },
    [table],
  );

  const handleSearchChange = useCallback(
    (value) => {
      table.setSearch(value);
      table.setPage(1);
    },
    [table],
  );

  const columns = useMemo(
    () =>
      buildBackToBackAccountsColumns(t, {
        serialOffset: (table.page - 1) * table.pageSize,
      }),
    [t, table.page, table.pageSize],
  );

  const toolbarActions = useMemo(
    () => [
      {
        id: 'add-back-to-back-account',
        label: t('BACK_TO_BACK_ADD'),
        icon: Plus,
        variant: ButtonVariants.primary,
        onClick: handleAdd,
      },
    ],
    [t, handleAdd],
  );

  const rowActions = useMemo(
    () => [
      {
        id: 'edit',
        label: t('COMMON_EDIT'),
        variant: ButtonVariants.tertiary,
        onClick: handleEdit,
      },
    ],
    [t, handleEdit],
  );

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('BACK_TO_BACK_EMPTY_TITLE'),
      description: t('BACK_TO_BACK_EMPTY_DESCRIPTION'),
      primaryLabel: t('BACK_TO_BACK_ADD'),
      primaryIcon: <Plus />,
      onPrimaryClick: handleAdd,
    }),
    [t, handleAdd],
  );

  const noResults = useMemo(
    () => ({
      title: t('BACK_TO_BACK_NO_RESULTS_TITLE'),
      description: t('COMMON_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  return (
    <>
      <Table
        columns={columns}
        data={rows}
        total={data?.totalCount ?? 0}
        loading={isFetching}
        getRowId={getAccountRowId}
        searchable
        onSortChange={handleSortChange}
        onSearchChange={handleSearchChange}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        toolbarActions={toolbarActions}
        rowActions={rowActions}
        actionsHeader={t('FIELD_ACTION')}
        emptyState={emptyState}
        noResults={noResults}
        labels={tableLabels}
      />

      <BackToBackAccountModal
        open={editTarget !== null}
        account={editTarget?.account}
        onClose={handleCloseModal}
      />
    </>
  );
};
